import { Job, SupabaseJob } from '@applicator/shared'

import { isJobRejectedFromRelevant, isJobRelevantForNext } from './relevanceHelpers'

// Helper function to check if search query is provided
export function hasSearchQuery(jobSearchQuery: string): boolean {
  return !!jobSearchQuery.trim()
}

// Helper function to normalize search query
export function normalizeSearchQuery(jobSearchQuery: string): string {
  return jobSearchQuery.toLowerCase().trim()
}

// Helper function to check if job matches search query
export function matchesSearchQuery(job: Job, searchLower: string): boolean {
  const positionMatch = job.position?.toLowerCase().includes(searchLower) || false
  const companyMatch = job.company?.toLowerCase().includes(searchLower) || false
  return positionMatch || companyMatch
}

// Helper function to filter jobs by search query
export function filterJobsBySearchQuery(jobsToFilter: Job[], jobSearchQuery: string): Job[] {
  if (!hasSearchQuery(jobSearchQuery)) return jobsToFilter
  const searchLower = normalizeSearchQuery(jobSearchQuery)
  return jobsToFilter.filter((job) => matchesSearchQuery(job, searchLower))
}

// Helper function to filter jobs by relevance for next job
export function filterJobsByRelevanceForNext(
  jobsToFilter: Job[],
  supabaseJobs: SupabaseJob[]
): Job[] {
  return jobsToFilter.filter((job) => {
    const supabaseJob = supabaseJobs.find(j => j.id === job.id)
    if (isJobRejectedFromRelevant(supabaseJob)) return false
    return isJobRelevantForNext(supabaseJob)
  })
}

// Helper function to get jobs after current
export function getJobsAfterCurrent(jobs: Job[], currentJob: Job | null): Job[] {
  if (!currentJob) return []
  const currentJobIndex = jobs.findIndex(j => j.id === currentJob.id)
  return jobs.slice(currentJobIndex + 1)
}

// Helper function to get first remaining job ID
export function getFirstRemainingJobId(remainingJobs: Job[]): string | null {
  return remainingJobs.length > 0 && remainingJobs[0].id ? remainingJobs[0].id : null
}

export function getNextRelevantJobId(
  currentJob: Job | null,
  jobs: Job[],
  jobSearchQuery: string,
  supabaseJobs: SupabaseJob[]
): string | null {
  if (!currentJob) return null
  const jobsAfterCurrent = getJobsAfterCurrent(jobs, currentJob)
  const filteredBySearch = filterJobsBySearchQuery(jobsAfterCurrent, jobSearchQuery)
  const filteredByRelevance = filterJobsByRelevanceForNext(filteredBySearch, supabaseJobs)
  return getFirstRemainingJobId(filteredByRelevance)
}
